/**
 * PlanLimitDialog — shell-mounted. Listens (axios interceptor) for 409
 * responses carrying `limit_code` (SEAT_LIMIT_REACHED / LOCATION_LIMIT_REACHED)
 * and offers: buy the add-on (prorated Razorpay charge), upgrade plan
 * (opens the plan picker) or contact us for Enterprise (locations).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Users, MapPin, Sparkles, Mail, X } from 'lucide-react';
import { useSubscription } from '../SubscriptionContext';
import { useAuth } from '../AuthContext';
import { loadRazorpay } from './razorpay';
import { EnterpriseEnquiryModal } from './EnterpriseEnquiryModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PlanLimitDialog() {
  const { user } = useAuth();
  const sub = useSubscription();
  const [limit, setLimit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [enqOpen, setEnqOpen] = useState(false);

  useEffect(() => {
    const id = axios.interceptors.response.use(null, (error) => {
      const d = error?.response?.data;
      if (error?.response?.status === 409 && d?.limit_code) { setNote(''); setLimit(d); }
      return Promise.reject(error);
    });
    const onOpen = (e) => { setNote(''); setLimit(e.detail); };
    window.addEventListener('audinexa:plan-limit', onOpen);
    return () => { axios.interceptors.response.eject(id); window.removeEventListener('audinexa:plan-limit', onOpen); };
  }, []);

  if (!limit) return null;
  const isSeat = limit.limit_code === 'SEAT_LIMIT_REACHED';
  const isOwner = ['clinic_owner', 'super_admin', 'founder'].includes(user?.role);
  const close = () => { setLimit(null); setBusy(false); };
  const openPlans = () => { close(); window.dispatchEvent(new CustomEvent('audinexa:open-plan-modal')); };

  const buyAddon = async () => {
    setBusy(true); setNote('');
    try {
      const r = await axios.post(`${API}/subscriptions/addon-checkout`, { addon: limit.addon, quantity: 1 });
      if (!r.data.razorpay_configured) { setNote('Payment gateway is not configured on this environment.'); setBusy(false); return; }
      await loadRazorpay();
      const rz = r.data.razorpay;
      const rzp = new window.Razorpay({
        key: rz.key_id, amount: rz.amount, currency: rz.currency, order_id: rz.order_id,
        name: 'AUDINEXA — Clinic OS',
        description: `${isSeat ? 'Extra user' : 'Extra location'} · prorated ${r.data.prorated_days} days`,
        prefill: { name: user?.name || '', email: user?.email || '' },
        modal: { ondismiss: () => setBusy(false) },
        handler: async (resp) => {
          try {
            await axios.post(`${API}/billing/tenant-invoices/${r.data.invoice_id}/razorpay/verify`, resp);
            sub.refresh && sub.refresh();
            window.dispatchEvent(new CustomEvent('audinexa:plan-usage-changed'));
            setNote(`${isSeat ? 'Seat' : 'Location'} added. You can try again now.`);
          } catch (e) {
            setNote('Verification failed: ' + (e?.response?.data?.detail || e.message));
          } finally { setBusy(false); }
        },
      });
      rzp.open();
    } catch (e) {
      const d = e?.response?.data?.detail || '';
      if (String(d).startsWith('CHOOSE_PLAN_FIRST')) { openPlans(); return; }
      setNote(d || 'Could not start the add-on purchase.');
      setBusy(false);
    }
  };

  const btn = 'w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-[13px] font-semibold transition-colors disabled:opacity-50';
  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center bg-slate-900/60 p-4" data-testid="plan-limit-dialog">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 relative">
        <button onClick={close} className="absolute right-3 top-3 text-slate-400 hover:text-slate-700" data-testid="plan-limit-close" aria-label="Close"><X size={16} /></button>
        <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center mb-3">
          {isSeat ? <Users size={18} /> : <MapPin size={18} />}
        </div>
        <h3 className="text-base font-bold text-slate-900 flex items-center gap-1.5">
          <AlertTriangle size={15} className="text-amber-500" /> {isSeat ? 'User seats are full' : 'Location limit reached'}
        </h3>
        <p className="text-[13px] text-slate-600 mt-2" data-testid="plan-limit-message">{limit.detail}</p>
        <p className="text-[11px] text-slate-400 mt-1" data-testid="plan-limit-usage">{limit.used} of {limit.limit} {isSeat ? 'seats' : 'locations'} in use</p>
        {note && <div className="mt-3 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid="plan-limit-note">{note}</div>}
        {!isOwner && <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">Ask your clinic owner to add {isSeat ? 'a seat' : 'a location'} or upgrade the plan.</div>}
        <div className="mt-5 space-y-2">
          <button onClick={buyAddon} disabled={busy || !isOwner} data-testid="plan-limit-addon-btn"
            className={`${btn} bg-indigo-600 hover:bg-indigo-700 text-white`}>
            {isSeat ? <Users size={14} /> : <MapPin size={14} />}
            {busy ? 'Opening payment…' : `Add ${isSeat ? 'a user' : 'a location'} · ₹${limit.addon_price}/month`}
          </button>
          <button onClick={openPlans} disabled={!isOwner} data-testid="plan-limit-upgrade-btn"
            className={`${btn} bg-white border border-slate-300 text-slate-800 hover:bg-slate-50`}>
            <Sparkles size={14} /> {isSeat ? 'Upgrade plan' : 'Upgrade to Chain'}
          </button>
          {!isSeat && (
            <button type="button" onClick={() => setEnqOpen(true)} data-testid="plan-limit-enterprise-btn"
              className={`${btn} bg-slate-900 hover:bg-slate-800 text-white`}>
              <Mail size={14} /> Contact us for Enterprise
            </button>
          )}
        </div>
        <EnterpriseEnquiryModal open={enqOpen} onClose={() => setEnqOpen(false)} source="limit_popup"
          prefill={{ name: user?.name || '', email: user?.email || '' }} />
        <p className="text-[10.5px] text-slate-400 mt-3 text-center">Add-ons are charged pro-rata for the rest of your current billing period, plus GST.</p>
      </div>
    </div>
  );
}
